/**
 * CaseStageSelect
 *
 * Reusable async-paginated dropdown for selecting a case stage.
 * Mirrors the EventSelect pattern — wraps react-select AsyncPaginate with the
 * getGlobalDropdown loader and shared AsyncSelect styles.
 *
 * Data source: getGlobalDropdown for 'case_stage', optionally filtered by practiceAreaId.
 * Caching:     React Query (via useQueryClient.fetchQuery) caches each page+search
 *              combination for 5 minutes. Reopening the dropdown is instant on a cache hit.
 *
 * Behaviour:
 *  - Single-select only.
 *  - The 'Closed' stage option is always disabled.
 *  - Re-fetches when practiceAreaId or isLeadSpecific changes (via cacheUniqs).
 *  - Searchable — passes inputValue to the API.
 *
 * @param {Object|null}  props.value             - Selected option object (with stage_name / case_stage_id), or null
 * @param {Function}     props.onChange           - Change handler; receives the full option object or null
 * @param {string}       [props.placeholder]
 * @param {boolean}      [props.isClearable]
 * @param {boolean}      [props.isDisabled]
 * @param {string}       [props.className]
 * @param {number}       [props.pageSize]         - Records per page (default: 15)
 * @param {string|null}  [props.practiceAreaId]   - Filters stages to a specific practice area
 * @param {boolean}      [props.isLeadSpecific]   - Passed to the API as a lead vs case filter
 * @param {string|null}  [props.excludeStageId]   - stage_id to render as disabled (e.g. current stage in split dialog)
 * @param {Element|null} [props.menuPortalTarget]  - Pass document.body when inside a dialog to escape overflow clipping
 * @param {string}       [props.menuPosition]      - react-select menu position ('absolute' | 'fixed')
 * @param {Object}       [props.styles]           - react-select style overrides
 * @param {string}       [props.name]
 * @param {string}       [props.id]
 * @param {Function}     [props.onBlur]
 * @param {Function}     [props.onFocus]
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { AsyncPaginate } from 'react-select-async-paginate';
import { useQueryClient } from '@tanstack/react-query';
import { fetchCaseStagesPage } from 'hooks/queries/useCaseStageDropdown';
import { queryKeys } from 'modules/cases/config/queryClient';
import '../shared/AsyncSelect.scss';
import './CaseStageSelect.scss';

const CLOSED = 'closed';

function isClosedOption(option) {
  return option?.stage_name?.toLowerCase() === CLOSED || option?.value?.toLowerCase() === CLOSED;
}

function StageOption({ innerRef, innerProps, isFocused, isSelected, isDisabled, children }) {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
        isDisabled ? 'async-select-custom__option--is-disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={`case-stage-option${isDisabled ? ' case-stage-option--disabled' : ''}`}>
        <span className="case-stage-option__label" title={children}>
          {children}
        </span>
      </div>
    </div>
  );
}

function CaseStageSelect({
  value = null,
  onChange,
  placeholder = 'Select Case Stage',
  isClearable = false,
  isDisabled = false,
  className = '',
  pageSize = 15,
  practiceAreaId = null,
  isLeadSpecific = false,
  excludeStageId = null,
  // Pass menuPortalTarget={document.body} + menuPosition="fixed" only when the
  // dropdown is inside a modal/dialog; leaving them null avoids the ResizeObserver
  // loop that react-select triggers when portalling to body unconditionally.
  menuPortalTarget = null,
  menuPosition = 'absolute',
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  ...rest
}) {
  const queryClient = useQueryClient();

  const isExcluded = useMemo(
    () => (opt) => excludeStageId != null && (opt?.case_stage_id === excludeStageId || opt?.sk === excludeStageId),
    [excludeStageId]
  );

  const loadOptions = useMemo(
    () => async (inputValue, _loadedOptions, additional) => {
      const page = (additional?.page ?? 0) + 1;

      // fetchQuery deduplicates in-flight requests and serves from cache when fresh.
      // staleTime matches the TTL so repeated opens within 5 min skip the network.
      const result = await queryClient.fetchQuery({
        queryKey: queryKeys.caseStages.dropdown(practiceAreaId, isLeadSpecific, inputValue, page, pageSize),
        queryFn: () => fetchCaseStagesPage({ practiceAreaId, isLeadSpecific, search: inputValue, page, pageSize }),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
      });

      const options = result.options.map((opt) => ({
        ...opt,
        isDisabled: isClosedOption(opt) || isExcluded(opt),
      }));

      return {
        options,
        hasMore: result.hasMore,
        additional: result.additional,
      };
    },
    [queryClient, practiceAreaId, isLeadSpecific, pageSize, isExcluded]
  );

  const mergedStyles = useMemo(
    () => ({
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      option: (base, { isDisabled: optDisabled }) => ({
        ...base,
        ...(optDisabled && {
          backgroundColor: 'var(--black-50, #f9fafb)',
          cursor: 'not-allowed',
          pointerEvents: 'auto',
        }),
      }),
      ...styles,
    }),
    [styles]
  );

  const components = useMemo(() => ({ Option: StageOption }), []);

  return (
    <span title={value?.stage_name}>
      <AsyncPaginate
        value={value}
        loadOptions={loadOptions}
        onChange={onChange}
        getOptionLabel={(option) => option?.stage_name || option?.label}
        getOptionValue={(option) => option?.case_stage_id || option?.value}
        isOptionDisabled={(opt) => isClosedOption(opt) || isExcluded(opt)}
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isSearchable
        className={`case-stage-select ${className}`.trim()}
        classNamePrefix="async-select-custom"
        styles={mergedStyles}
        components={components}
        cacheUniqs={[practiceAreaId, isLeadSpecific, excludeStageId]}
        additional={{ page: 0 }}
        debounceTimeout={300}
        loadingMessage={() => 'Loading...'}
        noOptionsMessage={() => 'No stages found'}
        menuShouldScrollIntoView={false}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPosition}
        name={name}
        id={id}
        onBlur={onBlur}
        onFocus={onFocus}
        {...rest}
      />
    </span>
  );
}

CaseStageSelect.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  className: PropTypes.string,
  pageSize: PropTypes.number,
  practiceAreaId: PropTypes.string,
  isLeadSpecific: PropTypes.bool,
  excludeStageId: PropTypes.string,
  menuPortalTarget: PropTypes.object,
  menuPosition: PropTypes.string,
  styles: PropTypes.object,
  name: PropTypes.string,
  id: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};

export default CaseStageSelect;
